import { useState, useEffect, useCallback } from 'react';
import { PropertyFilterProps } from '@cloudscape-design/components';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import moment from 'moment';

type PropertyFilterQuery = PropertyFilterProps.Query;
type PropertyFilterToken = PropertyFilterProps.Token;

// Generic type for the item being filtered
export type FilterableItem = Record<string, any>;

// Type for the field mapping function
export type FieldValueGetter<T> = (item: T, propertyKey: string) => any;

// Type for the search fields function
export type SearchFieldsGetter<T> = (item: T) => (string | number | boolean)[];

// Type for filter option
export type FilterOption = {
  propertyKey: string;
  value: any;
  label: string;
};

export enum FilterItemType {
  TEXT,
  NUMBER,
  DATE,
  BOOLEAN,
  LIST,
  NESTED_ARRAY,
}

export type NestedArrayConfig = {
  path: string;
  searchFields: string[];
};
// After (without trailing comma)
export const useGenericFilter = <T extends FilterableItem>(
  items: T[] | undefined,
  getItemValue: FieldValueGetter<T>,
  getSearchableFields: SearchFieldsGetter<T>,
  initialFilterOptions: any[] = [],
  nestedArrayConfig?: NestedArrayConfig,
  initialSearchText: string = '', // Add initial search text parameter
) => {
  const [filteredItems, setFilteredItems] = useState<T[] | undefined>(items);
  const [activeFilters, setActiveFilters] = useState<number>(0);
  const [searchText, setSearchText] = useState(initialSearchText); // Use initial value

  const [query, setQuery] = useState<PropertyFilterQuery>({
    tokens: [],
    operation: 'and',
  });

  const [filterOptions, setFilterOptions] =
    useState<FilterOption[]>(initialFilterOptions);

  // Helper function to search across specified fields
  const searchAllFields = useCallback(
    (item: T, searchValue: string): boolean => {
      if (!item || !searchValue) return false;

      const searchTerm = searchValue.toLowerCase();
      const searchableFields = getSearchableFields(item);

      return searchableFields.some((field) => {
        // Handle different types of fields
        if (field === null || field === undefined) return false;
        return String(field).toLowerCase().includes(searchTerm);
      });
    },
    [getSearchableFields],
  );

  // Generic function to filter nested arrays
  const filterNestedArrays = useCallback(
    (items: T[], searchTerm: string): T[] => {
      if (!nestedArrayConfig || !searchTerm) return items;

      return items.map((item) => {
        // Create a shallow copy of the item as a Record<string, any>
        const newItem = { ...item } as Record<string, any>;

        // Get the nested array using the path from config
        const nestedArray = newItem[nestedArrayConfig.path];

        // Check if the property exists and is an array
        if (nestedArray && Array.isArray(nestedArray)) {
          // Filter the nested array to only include matching items
          newItem[nestedArrayConfig.path] = nestedArray.filter((nestedItem) =>
            nestedArrayConfig.searchFields.some((field) =>
              String(nestedItem[field] || '')
                .toLowerCase()
                .includes(searchTerm),
            ),
          );
        }

        return newItem as T;
      });
    },
    [nestedArrayConfig],
  );

  // Helper function to safely convert value to string and lowercase
  const safeToLowerCase = (value: any): string => {
    try {
      if (value === null || value === undefined) return '';
      // Ensure value is converted to string before calling toLowerCase
      //return String(value).toLowerCase()

      return value;
    } catch (e) {
      return value;
    }
  };

  // Helper function to compare dates
  const compareDates = (date1: Date, date2: Date, operator: string): boolean => {
    // Convert to timestamps for comparison
    const timestamp1 = date1.getTime();
    const timestamp2 = date2.getTime();

    switch (operator) {
      case FILTER_OPERATORS.EQUALS:
        // Compare dates ignoring time - use UTC methods to avoid timezone issues
        return (
          date1.getUTCFullYear() === date2.getUTCFullYear() &&
          date1.getUTCMonth() === date2.getUTCMonth() &&
          date1.getUTCDate() === date2.getUTCDate()
        );
      case FILTER_OPERATORS.NOT_EQUALS:
        return (
          date1.getUTCFullYear() !== date2.getUTCFullYear() ||
          date1.getUTCMonth() !== date2.getUTCMonth() ||
          date1.getUTCDate() !== date2.getUTCDate()
        );
      case FILTER_OPERATORS.GREATER_THAN:
        return timestamp1 > timestamp2;
      case FILTER_OPERATORS.GREATER_THAN_OR_EQUAL:
        return timestamp1 >= timestamp2;
      case FILTER_OPERATORS.LESS_THAN:
        return timestamp1 < timestamp2;
      case FILTER_OPERATORS.LESS_THAN_OR_EQUAL:
        return timestamp1 <= timestamp2;
      default:
        return false;
    }
  };

  const checkTokenByItem = useCallback(
    (
      item: T,
      tokens: readonly PropertyFilterToken[],
      operation: string = 'and',
    ): boolean => {
      const checkFunction =
        operation === 'or' ? tokens.some.bind(tokens) : tokens.every.bind(tokens);
      return checkFunction((token) => {
        // Skip invalid tokens
        if (!token.propertyKey || token.value === undefined) {
          return true;
        }

        const { itemValue, type } = getItemValue(item, token.propertyKey);
        if (itemValue === undefined || itemValue === null) return false;

        // Handle date values - add this case before the existing date handling
        if (type === FilterItemType.DATE) {
          const itemDate = String(itemValue).includes('T')
            ? moment(itemValue).utc().startOf('day').toDate()
            : moment(Number(itemValue)).utc().startOf('day').toDate();
          let filterDate: Date;

          if (token.value instanceof Date) {
            filterDate = moment.utc(token.value).startOf('day').toDate();
          } else if (typeof token.value === 'number') {
            filterDate = moment.utc(token.value).startOf('day').toDate();
          } else if (typeof token.value === 'string') {
            if (!isNaN(Date.parse(token.value))) {
              filterDate = moment.utc(token.value).startOf('day').toDate();
            } else {
              return false;
            }
          } else {
            return false;
          }

          const operator = token.operator ?? FILTER_OPERATORS.EQUALS;
          return compareDates(itemDate, filterDate, operator);

          // Handle boolean values
        } else if (type === FilterItemType.BOOLEAN) {
          switch (token.operator) {
            case FILTER_OPERATORS.EQUALS:
              return itemValue.toString() === token.value.toString();
            case FILTER_OPERATORS.NOT_EQUALS:
              return itemValue.toString() !== token.value.toString();
            default:
              return true;
          }

          // Handle array values
        } else if (type === FilterItemType.LIST) {
          // Safely convert token value to string for comparison
          const filterValue = safeToLowerCase(token.value);
          const operator = token.operator ?? FILTER_OPERATORS.EQUALS;

          switch (operator) {
            case FILTER_OPERATORS.CONTAINS:
              return itemValue.some((value: any) =>
                safeToLowerCase(value).includes(filterValue),
              );
            case FILTER_OPERATORS.NOT_CONTAINS:
              return !itemValue.some((value: any) =>
                safeToLowerCase(value).includes(filterValue),
              );
            case FILTER_OPERATORS.EQUALS:
              return itemValue.some(
                (value: any) => safeToLowerCase(value) === filterValue,
              );
            case FILTER_OPERATORS.NOT_EQUALS:
              return !itemValue.some(
                (value: any) => safeToLowerCase(value) === filterValue,
              );
            default:
              return true;
          }
          // Handle numeric values
        } else if (type === FilterItemType.NUMBER) {
          const numValue = Number(token.value);
          const numItemValue = Number(itemValue);

          if (isNaN(numValue) || isNaN(numItemValue)) {
            return false;
          }

          const operator = token.operator ?? FILTER_OPERATORS.EQUALS;

          switch (operator) {
            case FILTER_OPERATORS.EQUALS:
              return numItemValue === numValue;
            case FILTER_OPERATORS.NOT_EQUALS:
              return numItemValue !== numValue;
            case FILTER_OPERATORS.GREATER_THAN:
              return numItemValue > numValue;
            case FILTER_OPERATORS.GREATER_THAN_OR_EQUAL:
              return numItemValue >= numValue;
            case FILTER_OPERATORS.LESS_THAN:
              return numItemValue < numValue;
            case FILTER_OPERATORS.LESS_THAN_OR_EQUAL:
              return numItemValue <= numValue;
            default:
              return true;
          }
        } else {
          // Handle scalar string values (default)
          const filterValue = safeToLowerCase(token.value);
          const actualValue = safeToLowerCase(itemValue);
          const operator = token.operator ?? FILTER_OPERATORS.EQUALS;

          switch (operator) {
            case FILTER_OPERATORS.CONTAINS:
              return actualValue.includes(filterValue);
            case FILTER_OPERATORS.NOT_CONTAINS:
              return !actualValue.includes(filterValue);
            case FILTER_OPERATORS.EQUALS:
              return actualValue === filterValue;
            case FILTER_OPERATORS.NOT_EQUALS:
              return actualValue !== filterValue;
            default:
              return true;
          }
        }
      });
    },
    [getItemValue],
  );
  // Main filtering logic
  const handleFiltering = useCallback(
    (
      items: T[],
      tokens: readonly PropertyFilterToken[],
      operation: string = 'and',
    ): T[] => {
      if (!tokens.length || !items.length) {
        return items;
      }

      const parentEntityFilters = tokens.filter((item) =>
        item.propertyKey?.startsWith('parentEntity.'),
      );

      const nestedItemFilters = tokens.filter((item) =>
        item.propertyKey?.startsWith('child.'),
      );

      // Handle direct property filters (like country, region, etc.)
      const directFilters = tokens.filter(
        (item) =>
          item.propertyKey &&
          !item.propertyKey.startsWith('parentEntity.') &&
          !item.propertyKey.startsWith('child.'),
      );

      // Combine parent entity and direct filters
      const allDirectFilters = [...parentEntityFilters, ...directFilters];

      // Create new array with filtered items
      return items
        .map((originalItem) => {
          // Create a deep copy to avoid modifying read-only objects
          const item = JSON.parse(JSON.stringify(originalItem));

          // Check if we need to filter nested arrays
          if (
            nestedItemFilters.length > 0 &&
            nestedArrayConfig &&
            item[nestedArrayConfig.path]
          ) {
            const nestedArray = item[nestedArrayConfig.path];

            if (Array.isArray(nestedArray)) {
              // Filter the nested array
              item[nestedArrayConfig.path] = nestedArray.filter((data) =>
                checkTokenByItem(data, nestedItemFilters, operation),
              );
            }
          }

          return item;
        })
        .filter((item) => {
          // Apply direct filters if they exist
          const passesDirectFilters =
            allDirectFilters.length === 0 ||
            checkTokenByItem(item, allDirectFilters, operation);

          // If we have nested item filters, check if the item should be included
          if (nestedItemFilters.length > 0 && nestedArrayConfig) {
            // For nested filtering, we want to keep the parent item if:
            // 1. It passes direct filters (if any)
            // 2. It has children that match the nested filters
            if (!passesDirectFilters) {
              return false;
            }

            // Always return true for parent items when we have nested filters
            // The nested arrays have already been filtered above
            return true;
          }

          return passesDirectFilters;
        });
    },
    [checkTokenByItem, nestedArrayConfig],
  );

  // Update filtered results when items or query changes
  useEffect(() => {
    if (items) {
      try {
        const filtered = handleFiltering(items, query.tokens, query.operation);
        setActiveFilters(query.tokens.length);
        setFilteredItems(filtered);
      } catch (error) {
        // console.error('Error filtering items:', error)
        setFilteredItems(items); // Fallback to showing all items on error
      }
    }
  }, [query, items, handleFiltering]);

  // Apply text search
  useEffect(() => {
    if (items) {
      // First apply token filtering
      let baseItems =
        query.tokens.length > 0
          ? handleFiltering(items, query.tokens, query.operation)
          : items;

      // Then apply text search if needed
      if (searchText && searchText !== '') {
        // First filter items based on all fields
        const filtered = baseItems.filter((item) => searchAllFields(item, searchText));

        // Then filter nested arrays within each item if config is provided
        const nestedFiltered = nestedArrayConfig
          ? filterNestedArrays(filtered, searchText.toLowerCase())
          : filtered;

        setFilteredItems(nestedFiltered);
      } else {
        setFilteredItems(baseItems);
      }
    }
  }, [
    searchText,
    query,
    items,
    handleFiltering,
    searchAllFields,
    filterNestedArrays,
    nestedArrayConfig,
  ]);

  return {
    searchText,
    setSearchText,
    activeFilters,
    query,
    setQuery,
    filteredItems,
    filterOptions,
    setFilterOptions,
  };
};
